import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { Navbar } from "../sections/Navbar";
import { Footer } from "../sections/Footer";
import { WhatsAppButton } from "../sections/WhatsAppButton";

// Shared shell for inner SEO pages (keeps luxury design, navbar, footer)
export const PageShell = ({ eyebrow, title, subtitle, breadcrumb = [], children }) => (
  <div className="App grain bg-[#050505] min-h-screen">
    <Navbar />
    <main>
      {/* Inner hero header */}
      <header className="relative pt-36 md:pt-44 pb-14 md:pb-16 overflow-hidden spotlight border-b border-white/10">
        <div className="absolute top-10 -left-20 w-72 h-72 rounded-full bg-[#d4af37]/12 blob z-0" />
        <div className="relative z-10 max-w-[1200px] mx-auto px-5 md:px-10">
          {breadcrumb.length > 0 && (
            <nav aria-label="Breadcrumb" className="flex items-center flex-wrap gap-1 text-xs text-[#888] mb-6">
              {breadcrumb.map((b, i) => (
                <span key={i} className="flex items-center gap-1">
                  {i > 0 && <ChevronRight size={12} className="text-[#555]" />}
                  {b.to ? (
                    <Link to={b.to} className="hover:text-[#d4af37] transition-colors">{b.label}</Link>
                  ) : (
                    <span className="text-[#d4af37]">{b.label}</span>
                  )}
                </span>
              ))}
            </nav>
          )}
          {eyebrow && (
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.35em] uppercase text-[#d4af37] mb-5">
              <span className="w-8 h-px bg-[#d4af37]/60" /> {eyebrow}
            </div>
          )}
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-4xl sm:text-5xl md:text-6xl text-white tracking-tight leading-[1.05] max-w-4xl"
          >
            {title}
          </motion.h1>
          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="mt-6 text-[#a3a3a3] text-base md:text-lg leading-relaxed max-w-2xl"
            >
              {subtitle}
            </motion.p>
          )}
        </div>
      </header>
      {children}
    </main>
    <Footer />
    <WhatsAppButton />
  </div>
);

export const CTASection = ({ text }) => (
  <section className="relative py-20 md:py-28 bg-[#080808]">
    <div className="max-w-[900px] mx-auto px-5 md:px-10 text-center">
      <div className="glass rounded-3xl px-8 py-12 md:py-16">
        <h2 className="font-display text-3xl md:text-4xl text-white leading-tight">{text}</h2>
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/#booking" data-testid="page-cta-book" className="rounded-full px-8 py-4 bg-[#d4af37] text-[#050505] font-medium hover:bg-[#e9d08a] transition-colors duration-300">
            Book a Consultation
          </Link>
          <Link to="/#contact" data-testid="page-cta-contact" className="rounded-full px-8 py-4 glass text-white hover:bg-white/10 transition-colors duration-300">
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  </section>
);
